import React from 'react';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';
import { useSearchHeader } from '@src/contexts/SearchHeaderContext';

interface NoSearchResultsProps {
  title?: string;
  message?: string;
  suggestions_title?: string;
  suggestions_list?: string;
  cta_text?: string;
  cta_link?: string;
  text_alignment?: 'left' | 'center' | 'right';
  background_color?: string;
  custom_css_class?: string;
  className?: string;
  settings?: any;
}

export const NoSearchResults: React.FC<NoSearchResultsProps> = ({
  title = 'No results found',
  message = "We couldn't find any results for your search. Try adjusting your search terms or browse our categories below.",
  cta_text = 'Browse All Products',
  cta_link = '/collections/all',
  text_alignment = 'center',
  background_color = 'transparent',
  custom_css_class = '',
  className = '',
  settings
}) => {
  const { isMobile, isTablet } = useResponsiveBreakpoints();
  const { products, collections, suggestedQueries, searchQuery } = useSearchHeader();

  // Only show when there are no results AND there is a search query
  const hasSearchQuery = searchQuery && searchQuery.length > 0;
  const hasProducts = products && products.length > 0;
  const hasCollections = collections && collections.length > 0;
  const hasSuggestions = suggestedQueries && suggestedQueries.length > 0;
  
  // Show no results when there's a query but no products
  const shouldShow = hasSearchQuery === true && hasProducts === false;

  // Don't render if there's no search query or if there are products
  if (!hasSearchQuery || hasProducts || !shouldShow) {
    return null;
  }

  // Parse suggestions list

  // Generate spacing classes from settings
  const spacingClasses = [
    settings?.padding_top ? `pt-[${settings.padding_top}px]` : '',
    settings?.padding_bottom ? `pb-[${settings.padding_bottom}px]` : '',
    settings?.padding_left ? `pl-[${settings.padding_left}px]` : '',
    settings?.padding_right ? `pr-[${settings.padding_right}px]` : '',
    settings?.margin_top ? `mt-[${settings.margin_top}px]` : '',
    settings?.margin_bottom ? `mb-[${settings.margin_bottom}px]` : '',
    settings?.margin_left ? `ml-[${settings.margin_left}px]` : '',
    settings?.margin_right ? `mr-[${settings.margin_right}px]` : ''
  ].filter(Boolean).join(' ');

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  return (
    <div 
      className={`w-full pb-6 ${isMobile || isTablet ? 'mb-8' : 'mb-5'} border-b border-[#E0E3E6] ${spacingClasses} ${alignmentClasses[text_alignment]} ${custom_css_class} ${className}`}
      style={{ backgroundColor: background_color !== 'transparent' ? background_color : undefined }}
    >
      {/* Title */}
      {title &&
        <h2 className={`mb-4 ${isMobile || isTablet ? 'h4' : 'h3'}`}>
          {title}
        </h2>
      }

      {/* Message */}
      {message &&
        <p className={`${isMobile || isTablet ? 'b1--bold' : 'b1--bold'}`}>
          {message} '{searchQuery}'
        </p>
      }

      {/* Call to Action */}
      {cta_text && cta_link && (
        <div>
          <a
            href={cta_link}
            className="button button-primary inline-flex items-center justify-center"
          >
            {cta_text}
          </a>
        </div>
      )}
    </div>
  );
};
